import React, { useState } from 'react';
import {
  Box, Typography, Button, List, ListItem, ListItemIcon, ListItemText,
  ListItemSecondaryAction, IconButton, LinearProgress, Paper,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export default function FileUpload({
  label,
  accept = '.pdf,.jpg,.jpeg,.png',
  multiple = true,
  maxFiles = 10,
  onFilesChange,
}) {
  const [files, setFiles] = useState([]);

  const notifyChange = (next) => {
    if (typeof onFilesChange === 'function') onFilesChange(next);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer?.files || []);
    addFiles(dropped);
  };

  const handleSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    addFiles(selected);
    e.target.value = '';
  };

  const addFiles = (newFiles) => {
    const withMeta = newFiles.map((f) => ({
      file: f,
      id: Math.random().toString(36).slice(2),
      name: f.name,
      size: (f.size / 1024).toFixed(1) + ' KB',
      progress: 100,
      uploaded: true,
    }));
    setFiles((prev) => {
      const next = [...prev, ...withMeta].slice(0, maxFiles);
      notifyChange(next);
      return next;
    });
  };

  const removeFile = (id) => setFiles((prev) => {
    const next = prev.filter((f) => f.id !== id);
    notifyChange(next);
    return next;
  });

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{label}</Typography>
      )}
      <Paper
        variant="outlined"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        sx={{
          p: 4, textAlign: 'center', borderStyle: 'dashed', borderColor: '#C4CDD5',
          borderRadius: 2, backgroundColor: '#FAFBFC', cursor: 'pointer',
          '&:hover': { borderColor: '#1B365D', backgroundColor: '#F0F4F8' },
          transition: 'all 0.2s',
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: '#90A4AE', mb: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Drag & drop files here, or
        </Typography>
        <Button variant="outlined" component="label" size="small" sx={{ borderRadius: 2 }}>
          Browse Files
          <input type="file" hidden accept={accept} multiple={multiple} onChange={handleSelect} />
        </Button>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          Supported: PDF, JPG, PNG (max {maxFiles} files)
        </Typography>
      </Paper>

      {files.length > 0 && (
        <List dense sx={{ mt: 1 }}>
          {files.map((f) => (
            <ListItem key={f.id} sx={{ borderRadius: 1, mb: 0.5, backgroundColor: '#F5F7FA' }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {f.uploaded ? (
                  <CheckCircleIcon sx={{ color: '#00B894', fontSize: 20 }} />
                ) : (
                  <InsertDriveFileIcon sx={{ fontSize: 20, color: '#90A4AE' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={f.name}
                secondary={f.size}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              <ListItemSecondaryAction>
                <IconButton size="small" onClick={() => removeFile(f.id)}>
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}
